import { useState, useRef, useMemo, Fragment } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, Globe, Type, Columns, Plus, X } from 'lucide-react';
import { Button } from '../Common';
import { csvImportAPI } from '../../api/endpoints';
import type { CSVAnalyzeResponse } from '../../types';
import { SearchableSelect } from './SearchableSelect';
import type { SelectOption } from './SearchableSelect';
import { CountrySelect } from '../Common/CountrySelect';

// Venue type options for the type dropdown
const VENUE_TYPES = [
  { value: '', label: 'None (no specific type)' },
  { value: 'concert_hall', label: 'Concert Hall' },
  { value: 'bar', label: 'Bar' },
  { value: 'concert_bar', label: 'Concert Bar' },
  { value: 'jazz_club', label: 'Jazz Club' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'city_concert_hall', label: 'City Concert Hall' },
  { value: 'festival', label: 'Festival' },
  { value: 'association', label: 'Association' },
  { value: 'city', label: 'City' },
  { value: 'comite_des_fetes', label: 'Comité des fêtes' },
  { value: 'competition', label: 'Competition' },
  { value: 'mentoring', label: 'Mentoring' },
  { value: 'booking_agency', label: 'Booking Agency' },
  { value: 'other', label: 'Other' },
];

type FieldInputType = 'column' | 'freeform' | 'select' | 'country';

interface SchemaField {
  key: string;
  label: string;
  required: boolean;
  group: string;
  allowFreeform: boolean;
  inputType?: FieldInputType;
  selectOptions?: { value: string; label: string }[];
}

const MAX_CONTACTS = 3;

// Schema fields in display order - grouped by category (no contact fields — rendered dynamically)
const SCHEMA_FIELDS: SchemaField[] = [
  // Venue fields
  { key: 'venue_name', label: 'Venue Name', required: true, group: 'venue', allowFreeform: false },
  { key: 'venue_url', label: 'Venue URL', required: false, group: 'venue', allowFreeform: false },
  { key: 'venue_type', label: 'Venue Type', required: false, group: 'venue', allowFreeform: false, inputType: 'select', selectOptions: VENUE_TYPES },
  // Address fields
  { key: 'venue_street', label: 'Street Address', required: false, group: 'address', allowFreeform: true },
  { key: 'venue_city', label: 'City', required: false, group: 'address', allowFreeform: true },
  { key: 'venue_zip_code', label: 'Postal Code', required: false, group: 'address', allowFreeform: true },
  { key: 'venue_admin_region_1', label: 'Department/County', required: false, group: 'address', allowFreeform: true },
  { key: 'venue_admin_region_2', label: 'Region/State', required: false, group: 'address', allowFreeform: true },
  { key: 'venue_country', label: 'Country', required: false, group: 'address', allowFreeform: true, inputType: 'country' },
  { key: 'venue_unparsed_address', label: 'Full Address (unparsed)', required: false, group: 'address', allowFreeform: true },
  // Venue details
  { key: 'venue_styles', label: 'Venue Styles', required: false, group: 'venue', allowFreeform: true },
  // Other
  { key: 'contact_period', label: 'Contact Period', required: false, group: 'other', allowFreeform: false },
  { key: 'concert_period', label: 'Concert Period', required: false, group: 'other', allowFreeform: false },
  { key: 'comments', label: 'Comments', required: false, group: 'other', allowFreeform: false },
];

// Template for contact field rows — applied per contact index N
interface ContactFieldTemplate {
  suffix: string;
  label: string;
  allowFreeform: boolean;
  inputType?: FieldInputType;
  selectOptions?: { value: string; label: string }[];
}

const CONTACT_FLAG_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'gatekeeper', label: 'Gatekeeper' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
  { value: 'expired', label: 'Expired' },
];

const CONTACT_FIELD_TEMPLATE: ContactFieldTemplate[] = [
  { suffix: 'name',  label: 'Contact Name',  allowFreeform: false },
  { suffix: 'email', label: 'Contact Email', allowFreeform: false },
  { suffix: 'phone', label: 'Contact Phone', allowFreeform: false },
  { suffix: 'role',  label: 'Contact Role',  allowFreeform: true  },
  { suffix: 'flag',  label: 'Contact Flag',  allowFreeform: false, inputType: 'select', selectOptions: CONTACT_FLAG_OPTIONS },
];

interface MappingStepProps {
  onAnalyzeComplete: (response: CSVAnalyzeResponse) => void;
  analyzeResponse: CSVAnalyzeResponse | null;
  confirmedMapping: Record<string, string | null>;
  onMappingChange: (mapping: Record<string, string | null>) => void;
  fixedValues: Record<string, string>;
  onFixedValuesChange: (fixedValues: Record<string, string>) => void;
  onConfirm: () => void;
}

export function MappingStep({
  onAnalyzeComplete,
  analyzeResponse,
  confirmedMapping,
  onMappingChange,
  fixedValues,
  onFixedValuesChange,
  onConfirm,
}: MappingStepProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Tracks which fields are in "freeform" mode (vs column mapping mode)
  const [freeformFields, setFreeformFields] = useState<Set<string>>(new Set());
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [contactCount, setContactCount] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build column options for the select dropdown, using index as value
  // to handle duplicate header names. Duplicate names get a suffix like "(col 3)".
  const columnOptions: SelectOption[] = useMemo(() => {
    if (!analyzeResponse) return [];
    const headers = analyzeResponse.headers;
    // Count occurrences of each header name
    const nameCounts = new Map<string, number>();
    for (const h of headers) {
      nameCounts.set(h, (nameCounts.get(h) || 0) + 1);
    }
    // Track which occurrence we're on for duplicate names
    const nameSeenCount = new Map<string, number>();
    return headers.map((h, idx) => {
      const count = nameCounts.get(h) || 1;
      if (count > 1) {
        const seen = (nameSeenCount.get(h) || 0) + 1;
        nameSeenCount.set(h, seen);
        return { value: String(idx), label: `${h} (col ${idx + 1})` };
      }
      return { value: String(idx), label: h };
    });
  }, [analyzeResponse]);

  const handleFileUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadedFileName(file.name);

    try {
      const response = await csvImportAPI.analyze(file);
      onAnalyzeComplete(response.data);

      // Build initial mapping: schema field -> column index (as string)
      const initialMapping: Record<string, string | null> = {};
      const prevConfirmed = response.data.previous_confirmed_mapping;
      if (prevConfirmed && Object.keys(prevConfirmed).length > 0) {
        // Use previous confirmed mapping directly (supports multiple fields sharing a column)
        for (const field of SCHEMA_FIELDS) {
          initialMapping[field.key] = prevConfirmed[field.key] ?? null;
        }
        // Infer contactCount from previous mapping keys
        let inferredCount = 1;
        for (let n = MAX_CONTACTS; n >= 2; n--) {
          if (CONTACT_FIELD_TEMPLATE.some((t) => prevConfirmed[`contact_${n}_${t.suffix}`] != null)) {
            inferredCount = n;
            break;
          }
        }
        setContactCount(inferredCount);
        for (let n = 1; n <= inferredCount; n++) {
          for (const t of CONTACT_FIELD_TEMPLATE) {
            const key = `contact_${n}_${t.suffix}`;
            initialMapping[key] = prevConfirmed[key] ?? null;
          }
        }
      } else {
        // Invert the suggested_mapping (which is header -> field) to field -> column index
        for (const field of SCHEMA_FIELDS) {
          const matchedEntry = Object.entries(response.data.suggested_mapping).find(
            ([, fieldName]) => fieldName === field.key
          );
          if (matchedEntry) {
            const headerIdx = response.data.headers.indexOf(matchedEntry[0]);
            initialMapping[field.key] = headerIdx >= 0 ? String(headerIdx) : null;
          } else {
            initialMapping[field.key] = null;
          }
        }
        // Initialize contact_1_* keys (default one contact block)
        setContactCount(1);
        for (const t of CONTACT_FIELD_TEMPLATE) {
          const key = `contact_1_${t.suffix}`;
          const matchedEntry = Object.entries(response.data.suggested_mapping).find(
            ([, fieldName]) => fieldName === key
          );
          if (matchedEntry) {
            const headerIdx = response.data.headers.indexOf(matchedEntry[0]);
            initialMapping[key] = headerIdx >= 0 ? String(headerIdx) : null;
          } else {
            initialMapping[key] = null;
          }
        }
      }
      onMappingChange(initialMapping);

      // Apply previous fixed values if reusing a mapping from a prior import
      const prevFixed = response.data.previous_fixed_values;
      if (prevFixed && Object.keys(prevFixed).length > 0) {
        onFixedValuesChange(prevFixed);
        // Toggle freeform mode for fields that have fixed values
        setFreeformFields((prev) => {
          const next = new Set(prev);
          for (const fieldKey of Object.keys(prevFixed)) {
            const field = SCHEMA_FIELDS.find((f) => f.key === fieldKey);
            if (field?.allowFreeform) {
              next.add(fieldKey);
            }
          }
          return next;
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to analyze CSV file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleMappingChange = (fieldKey: string, headerValue: string) => {
    onMappingChange({
      ...confirmedMapping,
      [fieldKey]: headerValue === '' ? null : headerValue,
    });
  };

  const handleToggleFreeform = (fieldKey: string) => {
    const newFreeform = new Set(freeformFields);
    if (newFreeform.has(fieldKey)) {
      // Switching back to column mode: clear fixed value
      newFreeform.delete(fieldKey);
      const newFixed = { ...fixedValues };
      delete newFixed[fieldKey];
      onFixedValuesChange(newFixed);
    } else {
      // Switching to freeform mode: clear column mapping
      newFreeform.add(fieldKey);
      onMappingChange({
        ...confirmedMapping,
        [fieldKey]: null,
      });
    }
    setFreeformFields(newFreeform);
  };

  const handleFixedValueChange = (fieldKey: string, value: string) => {
    onFixedValuesChange({
      ...fixedValues,
      [fieldKey]: value,
    });
  };

  const handleAddContact = () => {
    if (contactCount >= MAX_CONTACTS) return;
    const n = contactCount + 1;
    const newMapping = { ...confirmedMapping };
    for (const t of CONTACT_FIELD_TEMPLATE) {
      newMapping[`contact_${n}_${t.suffix}`] = null;
    }
    setContactCount(n);
    onMappingChange(newMapping);
  };

  const handleRemoveContact = (n: number) => {
    if (contactCount <= 1) return;
    const newMapping = { ...confirmedMapping };
    for (const t of CONTACT_FIELD_TEMPLATE) {
      delete newMapping[`contact_${n}_${t.suffix}`];
    }
    setContactCount(contactCount - 1);
    onMappingChange(newMapping);
  };

  // Check if venue_name is mapped
  const hasVenueNameMapping = !!confirmedMapping['venue_name'];

  // Get sample values for a selected column (sample_values is keyed by column index)
  const getSampleValues = (fieldKey: string): string[] => {
    const selectedIdx = confirmedMapping[fieldKey];
    if (!selectedIdx || !analyzeResponse?.sample_values) return [];
    return analyzeResponse.sample_values[selectedIdx] || [];
  };


  if (!analyzeResponse) {
    return (
      <div className="space-y-4">
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-dark-accent bg-dark-accent/10'
              : 'border-dark-border hover:border-dark-accent/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            className="hidden"
          />

          <Upload className="w-12 h-12 mx-auto text-dark-text-secondary mb-4" />
          <h3 className="text-lg font-medium text-dark-text-primary mb-2">
            Upload CSV File
          </h3>
          <p className="text-dark-text-secondary mb-4">
            Drag and drop your CSV file here, or click to browse
          </p>
          <p className="text-sm text-dark-text-muted mb-4">
            Maximum 40000 rows, 15MB file size
          </p>

          <Button
            onClick={() => fileInputRef.current?.click()}
            isLoading={isUploading}
            disabled={isUploading}
          >
            {isUploading ? 'Analyzing...' : 'Select File'}
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* File info */}
      <div className="flex items-center gap-3 p-3 bg-dark-elevated rounded-lg">
        <FileSpreadsheet className="w-8 h-8 text-dark-accent" />
        <div className="flex-1">
          <p className="font-medium text-dark-text-primary">
            {analyzeResponse.row_count} rows detected
          </p>
          <p className="text-sm text-dark-text-secondary">
            {analyzeResponse.headers.length} columns
          </p>
        </div>
        {analyzeResponse.inferred_country && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400">
              Detected country: <strong>{analyzeResponse.inferred_country}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Mapping table - Schema-centric */}
      <div>
        <h3 className="text-lg font-medium text-dark-text-primary mb-3">
          Column Mapping
        </h3>
        <p className="text-sm text-dark-text-secondary mb-4">
          Map your CSV columns to the data fields below. Fields marked with * are required.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-2 px-3 text-dark-text-secondary font-medium w-48">
                  Field
                </th>
                <th className="text-left py-2 px-3 text-dark-text-secondary font-medium w-72">
                  Value
                </th>
                <th className="text-left py-2 px-3 text-dark-text-secondary font-medium">
                  Sample Values
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Static groups: venue, address, other */}
              {['venue', 'address', 'other'].map((group) => {
                const groupFields = SCHEMA_FIELDS.filter((f) => f.group === group);
                const groupLabels: Record<string, string> = {
                  venue: 'Venue',
                  address: 'Address',
                  other: 'Other',
                };

                return (
                  <Fragment key={group}>
                    <tr className="bg-dark-elevated/50">
                      <td
                        colSpan={3}
                        className="py-2 px-3 text-xs font-semibold text-dark-text-muted uppercase tracking-wider"
                      >
                        {groupLabels[group]}
                      </td>
                    </tr>
                    {groupFields.map((field) => {
                      const samples = getSampleValues(field.key);
                      const isFreeform = freeformFields.has(field.key);
                      return (
                        <tr key={field.key} className="border-b border-dark-border/50">
                          <td className="py-3 px-3">
                            <span className="text-dark-text-primary font-medium">
                              {field.label}
                              {field.required && <span className="text-red-400 ml-1">*</span>}
                            </span>
                          </td>
                          <td className="py-3 px-3 w-72">
                            {field.inputType === 'select' && field.selectOptions ? (
                              <select
                                value={fixedValues[field.key] || ''}
                                onChange={(e) => handleFixedValueChange(field.key, e.target.value)}
                                className="input w-full"
                              >
                                {field.selectOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="flex items-center gap-2 w-full">
                                {field.allowFreeform && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleFreeform(field.key)}
                                    className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                                      isFreeform
                                        ? 'bg-dark-accent/20 text-dark-accent'
                                        : 'bg-dark-elevated text-dark-text-muted hover:text-dark-text-secondary'
                                    }`}
                                    title={isFreeform ? 'Switch to column mapping' : 'Enter a fixed value for all rows'}
                                  >
                                    {isFreeform ? <Columns className="w-4 h-4" /> : <Type className="w-4 h-4" />}
                                  </button>
                                )}
                                {isFreeform ? (
                                  field.inputType === 'country' ? (
                                    <CountrySelect
                                      value={fixedValues[field.key] || ''}
                                      onChange={(val) => handleFixedValueChange(field.key, val)}
                                      placeholder="Select country for all rows..."
                                      className="flex-1 min-w-0"
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={fixedValues[field.key] || ''}
                                      onChange={(e) => handleFixedValueChange(field.key, e.target.value)}
                                      placeholder={`Value for all rows...`}
                                      className="input flex-1 min-w-0"
                                    />
                                  )
                                ) : (
                                  <SearchableSelect
                                    value={confirmedMapping[field.key] || ''}
                                    onChange={(val) => handleMappingChange(field.key, val)}
                                    options={columnOptions}
                                    placeholder="Not mapped"
                                    className="flex-1 min-w-0"
                                  />
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {field.inputType === 'select' ? (
                              <span className="text-dark-text-muted text-sm italic">
                                Applied to all imported venues
                              </span>
                            ) : isFreeform ? (
                              <span className="text-dark-text-muted text-sm italic">
                                {fixedValues[field.key]
                                  ? `"${fixedValues[field.key]}" for all rows`
                                  : 'Enter a fixed value'}
                              </span>
                            ) : samples.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {samples.map((sample, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-block px-2 py-0.5 bg-dark-elevated rounded-sm text-xs text-dark-text-secondary max-w-[150px] truncate"
                                    title={sample}
                                  >
                                    {sample}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-dark-text-muted text-sm italic">
                                {confirmedMapping[field.key] ? 'No values' : 'Select a column'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}

              {/* Dynamic contact sections */}
              {Array.from({ length: contactCount }, (_, i) => i + 1).map((n) => (
                <Fragment key={`contact-block-${n}`}>
                  <tr className="bg-dark-elevated/50">
                    <td
                      colSpan={3}
                      className="py-2 px-3 text-xs font-semibold text-dark-text-muted uppercase tracking-wider"
                    >
                      <div className="flex items-center justify-between">
                        <span>Contact {n}</span>
                        {n > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveContact(n)}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm transition-colors"
                          >
                            <X className="w-3 h-3" />
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {CONTACT_FIELD_TEMPLATE.map((t) => {
                    const fieldKey = `contact_${n}_${t.suffix}`;
                    const samples = getSampleValues(fieldKey);
                    const isFreeform = freeformFields.has(fieldKey);
                    return (
                      <tr key={fieldKey} className="border-b border-dark-border/50">
                        <td className="py-3 px-3">
                          <span className="text-dark-text-primary font-medium">{t.label}</span>
                        </td>
                        <td className="py-3 px-3 w-72">
                          {t.inputType === 'select' && t.selectOptions ? (
                            <select
                              value={fixedValues[fieldKey] || ''}
                              onChange={(e) => handleFixedValueChange(fieldKey, e.target.value)}
                              className="input w-full"
                            >
                              {t.selectOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex items-center gap-2 w-full">
                              {t.allowFreeform && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleFreeform(fieldKey)}
                                  className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                                    isFreeform
                                      ? 'bg-dark-accent/20 text-dark-accent'
                                      : 'bg-dark-elevated text-dark-text-muted hover:text-dark-text-secondary'
                                  }`}
                                  title={isFreeform ? 'Switch to column mapping' : 'Enter a fixed value for all rows'}
                                >
                                  {isFreeform ? <Columns className="w-4 h-4" /> : <Type className="w-4 h-4" />}
                                </button>
                              )}
                              {isFreeform ? (
                                <input
                                  type="text"
                                  value={fixedValues[fieldKey] || ''}
                                  onChange={(e) => handleFixedValueChange(fieldKey, e.target.value)}
                                  placeholder="Value for all rows..."
                                  className="input flex-1 min-w-0"
                                />
                              ) : (
                                <SearchableSelect
                                  value={confirmedMapping[fieldKey] || ''}
                                  onChange={(val) => handleMappingChange(fieldKey, val)}
                                  options={columnOptions}
                                  placeholder="Not mapped"
                                  className="flex-1 min-w-0"
                                />
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {t.inputType === 'select' ? (
                            <span className="text-dark-text-muted text-sm italic">
                              Applied to all imported venues
                            </span>
                          ) : isFreeform ? (
                            <span className="text-dark-text-muted text-sm italic">
                              {fixedValues[fieldKey]
                                ? `"${fixedValues[fieldKey]}" for all rows`
                                : 'Enter a fixed value'}
                            </span>
                          ) : samples.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {samples.map((sample, idx) => (
                                <span
                                  key={idx}
                                  className="inline-block px-2 py-0.5 bg-dark-elevated rounded-sm text-xs text-dark-text-secondary max-w-[150px] truncate"
                                  title={sample}
                                >
                                  {sample}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-dark-text-muted text-sm italic">
                              {confirmedMapping[fieldKey] ? 'No values' : 'Select a column'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}

              {/* Add contact button row */}
              {contactCount < MAX_CONTACTS && (
                <tr className="border-b border-dark-border/50">
                  <td colSpan={3} className="py-2 px-3">
                    <button
                      type="button"
                      onClick={handleAddContact}
                      className="flex items-center gap-1.5 text-sm text-dark-accent hover:text-dark-accent/80 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Contact
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Source field */}
      {uploadedFileName && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody>
              <tr className="bg-dark-elevated/50">
                <td
                  colSpan={3}
                  className="py-2 px-3 text-xs font-semibold text-dark-text-muted uppercase tracking-wider"
                >
                  Source
                </td>
              </tr>
              <tr className="border-b border-dark-border/50">
                <td className="py-3 px-3">
                  <span className="text-dark-text-primary font-medium">Source</span>
                </td>
                <td className="py-3 px-3 w-72" />
                <td className="py-3 px-3">
                  <span className="text-dark-text-muted text-sm italic">
                    "{uploadedFileName}" for all rows
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Warning if no venue_name mapping */}
      {!hasVenueNameMapping && (
        <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>
            You must map a column to "Venue Name" to proceed.
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          onClick={() => {
            onAnalyzeComplete(null as any);
            onMappingChange({});
            setContactCount(1);
            setFreeformFields(new Set());
          }}
        >
          Upload Different File
        </Button>
        <Button onClick={onConfirm} disabled={!hasVenueNameMapping}>
          Confirm Mapping
        </Button>
      </div>
    </div>
  );
}
